import { View, Text, styled, ScrollView } from 'tamagui'
import { Sun, Moon, Monitor } from '@tamagui/lucide-icons'
import { useThemeController, type ThemeMode, type ThemeStyle } from '@repo/configs/themeController'
import { useAppStore } from '@/store'

const Container = styled(View, { flex: 1, flexDirection: 'column' })
const Header = styled(View, { paddingHorizontal: 32, paddingVertical: 20, borderBottomWidth: 1, borderBottomColor: '$borderDefault' })
const Title = styled(Text, { fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground' })
const Body = styled(View, { padding: 32, gap: 24, maxWidth: 600 })
const Section = styled(View, { gap: 12 })
const SectionTitle = styled(Text, { fontFamily: '$fontBody', fontWeight: '600', fontSize: 16, color: '$surfaceForeground' })
const SectionDesc = styled(Text, { fontFamily: '$fontBody', fontSize: 13, color: '$sidebarMuted' })

const OptionGrid = styled(View, { flexDirection: 'row', gap: 12 })

const OptionCard = styled(View, {
  flex: 1, padding: 16, borderRadius: '$borderRadiusXl', borderWidth: 2, borderColor: '$borderDefault',
  alignItems: 'center', gap: 8, cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
  // @ts-expect-error -- web transition
  style: { transition: 'border-color 150ms ease, background-color 150ms ease' },
  variants: { active: { true: { borderColor: '$accent', backgroundColor: '$accentLighter' } } } as const,
})

const modeIcons: Record<ThemeMode, typeof Sun> = { light: Sun, dark: Moon, system: Monitor }

export function AppearanceSettings() {
  const { themeMode, themeStyle, setThemeMode, setThemeStyle } = useThemeController()
  const { compactMode, setCompactMode } = useAppStore()
  const modes: ThemeMode[] = ['light', 'dark', 'system']

  return (
    <Container>
      <Header><Title>Appearance</Title></Header>
      <ScrollView flex={1}>
        <Body>
          <Section>
            <SectionTitle>Theme</SectionTitle>
            <SectionDesc>Choose how Arcus looks on your device.</SectionDesc>
            <OptionGrid>
              {modes.map((mode) => {
                const Icon = modeIcons[mode]
                return (
                  <OptionCard key={mode} active={themeMode === mode} onPress={() => setThemeMode(mode)}>
                    <Icon size={24} color={themeMode === mode ? '$accent' : '$sidebarMuted'} />
                    <Text fontFamily="$fontBody" fontSize={14} fontWeight="500"
                      color={themeMode === mode ? '$accent' : '$sidebarMuted'}>
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </Text>
                  </OptionCard>
                )
              })}
            </OptionGrid>
          </Section>

          <Section>
            <SectionTitle>Style</SectionTitle>
            <SectionDesc>Arcus' retro-futuristic look, or stripped-back brutalist.</SectionDesc>
            <OptionGrid>
              {([
                { value: 'arcus' as ThemeStyle, label: 'Arcus', desc: 'Cyan glows, spatial feel' },
                { value: 'brutalist' as ThemeStyle, label: 'Brutalist', desc: 'Flat, high-contrast' },
              ]).map((s) => (
                <OptionCard key={s.value} active={themeStyle === s.value} onPress={() => setThemeStyle(s.value)}>
                  <Text fontFamily="$fontHeading" fontSize={16} fontWeight="600"
                    color={themeStyle === s.value ? '$accent' : '$sidebarMuted'}>
                    {s.label}
                  </Text>
                  <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">{s.desc}</Text>
                </OptionCard>
              ))}
            </OptionGrid>
          </Section>
          <Section>
            <SectionTitle>Density</SectionTitle>
            <SectionDesc>Control how compact messages appear.</SectionDesc>
            <OptionGrid>
              <OptionCard active={!compactMode} onPress={() => setCompactMode(false)}>
                <Text fontFamily="$fontHeading" fontSize={16} fontWeight="600"
                  color={!compactMode ? '$accent' : '$sidebarMuted'}>
                  Cozy
                </Text>
                <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">Spacious, easy to read</Text>
              </OptionCard>
              <OptionCard active={compactMode} onPress={() => setCompactMode(true)}>
                <Text fontFamily="$fontHeading" fontSize={16} fontWeight="600"
                  color={compactMode ? '$accent' : '$sidebarMuted'}>
                  Compact
                </Text>
                <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">More messages on screen</Text>
              </OptionCard>
            </OptionGrid>
          </Section>
        </Body>
      </ScrollView>
    </Container>
  )
}
